import { useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { clientService } from "../../services/client.service";
import { validateEmail, validatePhone, validateGSTIN, validatePincode } from "../../utils/formValidation";

/**
 * Create Client Modal - 2026 SaaS Standard
 * 
 * Stripe/Linear inspired client creation modal.
 * Principles:
 * - Single dominant task
 * - Clear hierarchy through spacing
 * - Minimal color usage
 * - Predictable navigation
 * - Calm, confident tone
 */
export default function CreateClientModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    phone: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    gstin: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showTaxSection, setShowTaxSection] = useState(false);

  // Validate field on blur
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Client name is required';
        }
        break;

      case 'email':
        if (value && value.trim() !== '') {
          const validation = validateEmail(value);
          error = validation.error;
        }
        break;

      case 'phone':
        if (value && value.trim() !== '') {
          const validation = validatePhone(value);
          error = validation.error;
        }
        break;

      case 'gstin':
        if (value && value.trim() !== '') {
          const validation = validateGSTIN(value);
          error = validation.error;
        }
        break;

      case 'pincode':
        if (value && value.trim() !== '') {
          const validation = validatePincode(value);
          error = validation.error;
        }
        break;

      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, form[name]);
  };

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = async () => {
    // Validate all fields
    const fieldsToValidate = ['name', 'email', 'phone', 'gstin', 'pincode'];
    let isValid = true;
    const newErrors = {};

    fieldsToValidate.forEach(field => {
      const fieldValue = form[field];
      // Re-validate to get fresh error state
      const hasError = !validateField(field, fieldValue);
      if (hasError) {
        isValid = false;
        newErrors[field] = errors[field];
      }
    });

    // Mark all fields as touched
    const allTouched = fieldsToValidate.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // If validation fails, stop here and keep modal open
    if (!isValid) {
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => element.focus(), 300);
      }
      return; // Don't close modal, don't save
    }

    setIsSaving(true);

    try {
      const result = await clientService.add({
        ...form,
        gstin: form.gstin || null,
      });

      // Create client object for immediate selection
      const createdClient = {
        id: result.lastInsertRowid,
        name: form.name,
        contact_name: form.contact_name,
        phone: form.phone,
        email: form.email,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        country: form.country,
        gstin: form.gstin || null,
        notes: form.notes,
      };

      onSave(createdClient); // This closes the modal
    } catch (err) {
      console.error("Failed to create client:", err);
      alert(`Failed to create client: ${err.message}`);
      setIsSaving(false); // Re-enable save button on error
    }
    // Note: Don't set isSaving to false on success, modal will close
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const getFieldClassName = (fieldName) => {
    const baseClass = "w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors";
    
    if (touched[fieldName] && errors[fieldName]) {
      return `${baseClass} border-red-300 focus:border-red-500 focus:ring-red-500/20`;
    }
    
    return `${baseClass} border-slate-300 focus:ring-slate-900/10 focus:border-slate-400`;
  };

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onKeyDown={handleKeyDown}
      style={{ animation: "fadeIn 0.15s ease-out" }}
    >
      {/* Modal Container */}
      <div 
        className="bg-white w-full max-w-[780px] max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          animation: "scaleIn 0.18s ease-out",
          border: "1px solid rgb(226, 232, 240)"
        }}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Create client
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Used for invoices and GST
            </p>
          </div>
          
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body - Scrollable */}
        <div className="flex-1 overflow-auto px-8 py-8">
          <div className="max-w-[600px] space-y-8">
            
            {/* Section 1: Identity (Primary - Strongest emphasis) */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Client name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoFocus
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  className={getFieldClassName('name')}
                  placeholder="e.g. ABC Corporation"
                />
                {touched.name && errors.name && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.name}
                  </p>
                )}
                {!errors.name && (
                  <p className="text-xs text-slate-500 mt-2">
                    This is how it will appear on invoices
                  </p>
                )}
              </div>
            </div>

            {/* Section 2: Contact Information (Secondary - Neutral weight) */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-700">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Contact name
                  </label>
                  <input
                    type="text"
                    value={form.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors"
                    placeholder="Contact person name"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => {
                      // Only allow numbers, spaces, +, -, (, )
                      const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                      handleChange('phone', value);
                    }}
                    onBlur={() => handleBlur('phone')}
                    onKeyPress={(e) => {
                      // Prevent non-numeric characters (except +, -, space, parentheses)
                      if (!/[0-9+\-\s()]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className={getFieldClassName('phone')}
                    placeholder="XXXXXXXXXX"
                  />
                  {touched.phone && errors.phone ? (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.phone}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">
                      Numbers only
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-600 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  className={getFieldClassName('email')}
                  placeholder="email@example.com"
                />
                {touched.email && errors.email && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Section 3: Address (Tertiary - Lighter background) */}
            <div className="space-y-4 p-5 bg-slate-50/50 rounded-lg border border-slate-100">
              <h3 className="text-sm font-medium text-slate-700">
                Address
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Address line 1
                  </label>
                  <input
                    type="text"
                    value={form.address_line1}
                    onChange={(e) => handleChange('address_line1', e.target.value)}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors bg-white"
                    placeholder="123 Business Street"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">
                    Address line 2
                  </label>
                  <input
                    type="text"
                    value={form.address_line2}
                    onChange={(e) => handleChange('address_line2', e.target.value)}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors bg-white"
                    placeholder="Suite 100"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors bg-white"
                      placeholder="Mumbai"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-600 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={form.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors bg-white"
                      placeholder="MH"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-600 mb-2">
                      ZIP
                    </label>
                    <input
                      type="text"
                      value={form.pincode}
                      onChange={(e) => {
                        // Only allow numbers for pincode
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 6) {
                          handleChange('pincode', value);
                        }
                      }}
                      onBlur={() => handleBlur('pincode')}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      className={getFieldClassName('pincode')}
                      placeholder="400001"
                      maxLength={6}
                    />
                    {touched.pincode && errors.pincode && (
                      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {errors.pincode}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Optional Section: Tax & GST (Collapsible) */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowTaxSection(!showTaxSection)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
              >
                <div>
                  <div className="text-sm font-medium text-slate-700">
                    Tax & GST
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Optional — Add GSTIN if applicable
                  </div>
                </div>
                <ChevronDown 
                  size={16} 
                  className={`text-slate-400 transition-transform ${showTaxSection ? 'rotate-180' : ''}`}
                />
              </button>

              {showTaxSection && (
                <div className="px-5 py-4 border-t border-slate-200 bg-slate-50/30">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">
                      GSTIN
                    </label>
                    <input
                      type="text"
                      value={form.gstin}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
                        if (value.length <= 15) {
                          handleChange('gstin', value);
                        }
                      }}
                      onBlur={() => handleBlur('gstin')}
                      className={getFieldClassName('gstin')}
                      placeholder="27XXXXX1234X1Z5"
                      maxLength={15}
                    />
                    {touched.gstin && errors.gstin ? (
                      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {errors.gstin}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500 mt-2">
                        15-character GST identification number
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Footer - Sticky, Subtle contrast */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-slate-200 bg-slate-50/50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            Back
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving || !form.name.trim() || Object.values(errors).some(e => e)}
            className="px-5 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save & select client"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.96) translateY(-8px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
